import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { connect } from 'react-redux';
import {
  FlexCol,
  FlexRow,
  ArticleList,
  SitemapList,
  Pagination,
} from '@bart.sk-eshop/eshop-defaults';
import MetaTags from '../../components/_helpers/MetaTags/MetaTags';
import {
  buildListingCanonicalUrl,
  buildMetaDescription,
  buildSectionMetaDescription,
  normalizeMetaText,
  NOINDEX_FOLLOW,
  shouldNoIndexByUrlParams,
} from '../../utilities/metatags';
import { loadUrlTranslations } from '../App/actions';
import { connectSsr } from '@bart.sk-eshop/ssr-service';
import { __ } from '@bart.sk-eshop/react-i18n';
import {
  loadCMSCategoriesTree,
  loadCMSArticles,
  loadCMSSectionMeta,
} from './actions';
import {
  sitemapSelector,
  allArticlesSelector,
  sitemapIsFetchingSelector,
  articlesIsFetchingSelector,
  sitemapByIdSelector,
  sectionMetaSelector,
} from './selectors';
import API, { ThenArg } from '../../services/API';
import { withRouter, WithRouterProps } from 'react-router';
import { push } from 'react-router-redux';
import { prop } from '../../utilities';
import {
  BreadCrumbType,
  setBreadCrumbPath,
} from '../BreadCrumb/breadCrumbSlice';
import ToTopButton from '../../components/_helpers/Default/ToTopButton';

interface CMSConfig {
  sectionId: string;
  title: string;
  breadcrumbType: BreadCrumbType;
  crossLink?: { name: string; pathname: string };
}

interface Props {
  dispatch: (action: any) => void;
  sitemap: ThenArg<typeof API.loadSitemapTree>['sitemap_tree'];
  isFetching: boolean;
  allArticles: ThenArg<typeof API.loadArticles>;
  isFetchingArticles: boolean;
  sitemapsById: object;
  sectionMeta: any;
  lang: string;
}

export function createCMSArticles(config: CMSConfig) {
  class CMSArticlesComponent extends React.Component<Props & WithRouterProps> {
    public static async getInitialProps(parentProps) {
      try {
        const { dispatch, location } = parentProps;
        const {
          query: { offset, subid },
        } = location;
        await dispatch(loadCMSCategoriesTree(config.sectionId));
        await dispatch(loadCMSSectionMeta(config.sectionId));
        await dispatch(loadCMSArticles(config.sectionId, subid, 6, offset));
        await dispatch(
          loadUrlTranslations(location.pathname, location.pathname, 'sitemap'),
        );
        dispatch(setBreadCrumbPath(config.breadcrumbType));
        return;
      } catch (exp) {
        return {
          isError: true,
        };
      }
    }

    public render() {
      return (
        <>
          <Wrapper className="container container--wide">
            <MetaTags
              tags={this.getMetaTags()}
              canonicalUrl={buildListingCanonicalUrl(
                prop(this.props.location, 'pathname'),
                prop(this.props.location, 'query', {}),
                this.props.lang,
              )}
              robots={
                shouldNoIndexByUrlParams(prop(this.props.location, 'query', {}))
                  ? NOINDEX_FOLLOW
                  : undefined
              }
            />
            {this.renderCategories()}
            {this.renderArticles()}
          </Wrapper>
          <ToTopButton />
        </>
      );
    }

    public getMetaTags = () => {
      const { sectionMeta, lang } = this.props;
      const name = prop(sectionMeta, 'name') || __(config.title);

      return {
        title: normalizeMetaText(prop(sectionMeta, 'meta.title')) || name,
        description:
          buildMetaDescription([prop(sectionMeta, 'meta.description')]) ||
          buildSectionMetaDescription(name, lang),
      };
    };

    public renderCategories = () => {
      const { sitemap } = this.props;
      const subid = this.props.location.query.subid;
      const remappedSitemap = (Array.isArray(sitemap) ? sitemap : []).map(
        s => ({
          name: s.name || '',
          id: s.id || '',
        }),
      );

      return (
        <SitemapList
          currentId={(subid && subid.toString()) || -1}
          handleCategoryChange={this.handleCategoryChange}
          sitemaps={remappedSitemap}
          isBlog={true}
          title={__(config.title)}
          crossLink={config.crossLink}
        />
      );
    };

    public renderArticles = () => {
      const {
        allArticles,
        isFetchingArticles,
        location,
        sitemapsById,
      } = this.props;
      const articles = prop(allArticles, 'articles');
      const total = prop(allArticles, 'total', 0);
      const offset = prop(location, 'query.offset', 0);

      return (
        <ContentWrapper>
          <ArticlesWrapper>
            <ArticleList
              sitemapsById={sitemapsById}
              articles={articles}
              isFetching={isFetchingArticles}
            />
            <Pagination
              query={this.getPaginationQuery()}
              totalItems={total}
              limit={6}
              offset={offset}
              onOffsetChange={this.handleOffsetChange}
            />
          </ArticlesWrapper>
        </ContentWrapper>
      );
    };

    public handleOffsetChange = (newOffset: number) => {
      const subid = this.props.location.query.subid;
      const searchQuery = subid
        ? `?subid=${subid}&offset=${newOffset}`
        : `?offset=${newOffset}`;
      this.props.dispatch(
        push({
          pathname: this.props.location.pathname,
          search: searchQuery,
        }),
      );
    };

    public handleCategoryChange = async (newId: number | string) => {
      const { location, dispatch, sitemap } = this.props;
      const target = (Array.isArray(sitemap) ? sitemap : []).find(
        s => s.id && s.id.toString() === newId.toString(),
      );
      const url = prop(target, 'url');

      dispatch(
        push({
          pathname: url ? `/${url}` : location.pathname,
          search: '',
        }),
      );
    };

    public getPaginationQuery = () => {
      const {
        location: {
          pathname,
          query: { subid },
        },
      } = this.props;
      return subid ? `${pathname}?subid=${subid}` : `${pathname}?`;
    };
  }

  const mapStateToProps = state => ({
    sitemap: sitemapSelector(state, config.sectionId),
    isFetching: sitemapIsFetchingSelector(state, config.sectionId),
    allArticles: allArticlesSelector(state, config.sectionId),
    isFetchingArticles: articlesIsFetchingSelector(state, config.sectionId),
    sitemapsById: sitemapByIdSelector(state, config.sectionId),
    sectionMeta: sectionMetaSelector(state, config.sectionId),
    lang: state.general.lang,
  });

  return connect(mapStateToProps)(
    connectSsr({ displayName: `CMSArticles_${config.sectionId}` })(
      withRouter(CMSArticlesComponent),
    ),
  );
}

const Wrapper = styled(FlexRow)`
  width: 100%;
  padding: ${rem(56)} ${rem(24)};

  ${({ theme }) => theme.mediab.l925`
    flex-flow: column;
    padding: 0 ${rem(24)} ${rem(32)};
  `}
`;

const ContentWrapper = styled(FlexCol)`
  width: 100%;
  justify-content: flex-end;

  ${({ theme }) => theme.mediab.l925`
     justify-content: center;
  `}
`;

const ArticlesWrapper = styled(FlexCol)`
  width: 100%;
  max-width: ${rem(1048)};
  margin-right: 0;
  margin-left: auto;
`;

export default createCMSArticles({
  sectionId: 'MAGAZIN',
  title: 'Magazín',
  breadcrumbType: BreadCrumbType.CMS_ARTICLES,
  crossLink: { name: 'Blog', pathname: '/blog' },
});
